import Image from "next/image";
import { useRouter } from "next/navigation";
import { useState, useEffect } from "react";
import {
  SparklesIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";

// ────────────────────────────────────────────────────────────────
// Suggestion Chips
// ────────────────────────────────────────────────────────────────

const SEARCH_SUGGESTIONS = [
  "Gentle yoga for beginners",
  "30 min morning meditation",
  "High energy zumba",
  "Yoga for back pain",
];

// ────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────

const HomePageBanner: React.FC = () => {
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState("");
  const [placeholder, setPlaceholder] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Typewriter
  const placeholderTexts = [
    "Gentle yoga for back pain...",
    "30 minute morning meditation...",
    "High energy dance workout...",
    "Affordable online yoga class...",
    "Relaxing evening session...",
    "Beginner friendly pilates...",
  ];

  useEffect(() => {
    let currentTextIndex = 0;
    let currentCharIndex = 0;
    let isDeleting = false;
    let typingSpeed = 100;
    let timeoutId: NodeJS.Timeout;

    const type = () => {
      const currentText = placeholderTexts[currentTextIndex];
      if (isDeleting) {
        setPlaceholder(currentText.substring(0, currentCharIndex - 1));
        currentCharIndex--;
        typingSpeed = 50;
      } else {
        setPlaceholder(currentText.substring(0, currentCharIndex + 1));
        currentCharIndex++;
        typingSpeed = 100;
      }
      if (!isDeleting && currentCharIndex === currentText.length) {
        isDeleting = true;
        typingSpeed = 2000;
      } else if (isDeleting && currentCharIndex === 0) {
        isDeleting = false;
        currentTextIndex = (currentTextIndex + 1) % placeholderTexts.length;
        typingSpeed = 500;
      }
      timeoutId = setTimeout(type, typingSpeed);
    };

    if (!isFocused && !searchQuery) {
      timeoutId = setTimeout(type, 1000);
    } else {
      setPlaceholder("Search wellness classes...");
    }
    return () => clearTimeout(timeoutId);
  }, [isFocused, searchQuery]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q) {
      router.push(`/classes?ai_search=${encodeURIComponent(q)}`);
    } else {
      router.push("/classes");
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    router.push(`/classes?ai_search=${encodeURIComponent(suggestion)}`);
  };

  return (
    <section
      className="relative w-full overflow-hidden min-h-[100vh] flex items-center"
      style={{ background: "linear-gradient(168deg, #fdfcfb 0%, #f7f3ef 40%, #f0ebe4 100%)" }}
      id="hero-banner"
    >
      {/* ═══ Breathing Circle — ambient background element ═══ */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
        <div
          className="w-[700px] h-[700px] lg:w-[900px] lg:h-[900px] rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(255,112,67,0.04) 0%, rgba(255,140,66,0.02) 40%, transparent 70%)",
            animation: "breathe 8s ease-in-out infinite",
          }}
        />
      </div>

      {/* Subtle organic shapes */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <svg
          className="absolute -top-20 -right-20 w-[400px] h-[400px] opacity-[0.03]"
          viewBox="0 0 200 200"
          style={{ animation: "spin-slow 60s linear infinite" }}
        >
          <path
            fill="#FF7043"
            d="M44.7,-76.4C58.8,-69.2,71.8,-59.1,79.6,-45.8C87.4,-32.5,90,-16.3,88.5,-0.9C87,14.5,81.4,29,73.1,41.6C64.8,54.2,53.8,64.9,40.8,72.4C27.8,79.9,12.9,84.2,-1.6,86.9C-16.1,89.6,-32.2,90.7,-44.9,83.1C-57.6,75.5,-66.9,59.2,-74.2,43.2C-81.5,27.2,-86.8,11.6,-85.7,-3.6C-84.6,-18.8,-77.1,-33.6,-67.1,-46.2C-57.1,-58.8,-44.6,-69.2,-31,-75.6C-17.4,-82,-3.7,-84.4,10.5,-83.4C24.7,-82.4,30.6,-83.6,44.7,-76.4Z"
            transform="translate(100 100)"
          />
        </svg>
        <svg
          className="absolute -bottom-32 -left-32 w-[500px] h-[500px] opacity-[0.025]"
          viewBox="0 0 200 200"
          style={{ animation: "spin-slow 80s linear infinite reverse" }}
        >
          <path
            fill="#8fbc8f"
            d="M39.9,-67.7C52.4,-61.3,63.6,-51.7,72.5,-39.6C81.4,-27.5,87.9,-13.8,87.7,-0.1C87.5,13.5,80.6,27.1,72.1,39.5C63.6,51.9,53.5,63.2,41,70.1C28.5,77,14.3,79.5,0.3,79C-13.7,78.5,-27.4,75,-40.1,68.2C-52.8,61.4,-64.5,51.3,-72.4,38.5C-80.3,25.7,-84.4,10.2,-83.1,-4.8C-81.8,-19.8,-75.1,-34.3,-65.3,-45.8C-55.5,-57.3,-42.6,-65.8,-29.4,-71.8C-16.2,-77.8,-2.7,-81.3,10.3,-80.3C23.3,-79.3,27.4,-74.1,39.9,-67.7Z"
            transform="translate(100 100)"
          />
        </svg>
      </div>

      {/* ═══ Main Content ═══ */}
      <div className="container mx-auto px-6 lg:px-12 relative z-10 max-w-[1300px] pt-[120px] pb-16 lg:pt-[140px] lg:pb-20">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-10 lg:gap-6">

          {/* ─── Left Column ─── */}
          <div
            className="w-full lg:w-[50%] flex flex-col items-start text-left"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(30px)",
              transition: "all 0.9s cubic-bezier(0.16, 1, 0.3, 1)",
            }}
          >
            {/* AI Badge */}
            <div
              onClick={() => router.push("/ai-assistant")}
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full cursor-pointer mb-7 group"
              style={{
                background: "rgba(255,112,67,0.06)",
                border: "1px solid rgba(255,112,67,0.12)",
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateY(0)" : "translateY(15px)",
                transition: "all 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.15s",
              }}
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF7043] opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#FF7043]" />
              </span>
              <span className="text-[11px] font-semibold text-[#c4501f] tracking-wide">
                Prana AI — Your Wellness Copilot
              </span>
              <SparklesIcon className="w-3 h-3 text-[#FF7043] group-hover:rotate-12 transition-transform" />
            </div>

            {/* Headline */}
            <h1
              className="text-[22px] sm:text-[26px] lg:text-[30px] xl:text-[34px] font-semibold text-[#2a2a2a] leading-[1.2] tracking-[-0.02em] mb-4"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateY(0)" : "translateY(20px)",
                transition: "all 0.9s cubic-bezier(0.16, 1, 0.3, 1) 0.2s",
              }}
            >
              Move well. Feel well.{" "}
              <span
                className="inline"
                style={{
                  background: "linear-gradient(135deg, #FF7043, #e8683a)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Live well.
              </span>
            </h1>

            {/* Subtle separator */}
            <div
              className="w-12 h-[2px] rounded-full mb-5"
              style={{
                background: "linear-gradient(90deg, #FF7043, transparent)",
                opacity: isVisible ? 1 : 0,
                transition: "opacity 0.8s ease 0.4s",
              }}
            />

            {/* Sub-headline */}
            <p
              className="text-[14px] sm:text-[15px] text-[#888] mb-9 max-w-[420px] leading-[1.75] font-normal"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateY(0)" : "translateY(15px)",
                transition: "all 0.9s cubic-bezier(0.16, 1, 0.3, 1) 0.35s",
              }}
            >
              Expert-led yoga, meditation & holistic wellness — live or
              on-demand. Discover classes that fit your body, your schedule,
              and your goals.
            </p>

            {/* ── Search ── */}
            <div
              className="w-full max-w-[480px] relative group"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateY(0)" : "translateY(15px)",
                transition: "all 0.9s cubic-bezier(0.16, 1, 0.3, 1) 0.45s",
              }}
            >
              <div
                className="absolute -inset-[1px] rounded-2xl transition-opacity duration-500"
                style={{
                  background: "linear-gradient(135deg, rgba(255,112,67,0.15), rgba(255,140,66,0.05))",
                  filter: "blur(8px)",
                  opacity: isFocused ? 1 : 0,
                }}
              />
              <div className="relative bg-white/80 backdrop-blur-sm border border-[#e8e4df] rounded-2xl p-1.5 flex items-center shadow-[0_1px_12px_rgba(0,0,0,0.03)] hover:shadow-[0_4px_24px_rgba(0,0,0,0.06)] transition-all duration-500">
                <div className="pl-4 pr-2">
                  <MagnifyingGlassIcon className="w-[18px] h-[18px] text-[#bbb]" />
                </div>
                <form onSubmit={handleSearch} className="flex-1">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    placeholder={placeholder}
                    className="w-full py-2.5 px-2 text-[#2a2a2a] bg-transparent focus:outline-none text-[14px] placeholder-[#bbb] font-medium"
                    id="hero-search-input"
                  />
                </form>
                <button
                  onClick={handleSearch}
                  className="text-white px-5 py-2.5 rounded-xl text-[13px] font-semibold transition-all duration-300 hover:opacity-90 active:scale-[0.97]"
                  style={{ background: "#2a2a2a" }}
                  id="hero-search-button"
                >
                  Explore
                </button>
              </div>
            </div>

            {/* Trending */}
            <div
              className="mt-4 flex flex-wrap items-center gap-2"
              style={{
                opacity: isVisible ? 1 : 0,
                transition: "opacity 0.9s ease 0.55s",
              }}
            >
              <span className="text-[10px] font-semibold uppercase tracking-widest text-[#bbb]">
                Popular
              </span>
              {SEARCH_SUGGESTIONS.slice(0, 3).map((s) => (
                <button
                  key={s}
                  onClick={() => handleSuggestionClick(s)}
                  className="text-[10px] text-[#999] bg-white/60 border border-[#e8e4df] hover:border-[#FF7043]/25 hover:text-[#c4501f] hover:bg-white px-2.5 py-1 rounded-full transition-all duration-300"
                >
                  {s}
                </button>
              ))}
            </div>

            {/* ── Small trust indicators (no numbers) ── */}
            <div
              className="mt-10 flex items-center gap-5"
              style={{
                opacity: isVisible ? 1 : 0,
                transition: "opacity 0.9s ease 0.65s",
              }}
            >
              {[
                { icon: "🧘", text: "Yoga & Meditation" },
                { icon: "🎯", text: "Personalized Plans" },
                { icon: "✨", text: "AI-Powered" },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-1.5">
                  <span className="text-[13px]">{item.icon}</span>
                  <span className="text-[11px] text-[#999] font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ─── Right Column: Asymmetric Bento Grid ─── */}
          <div
            className="w-full lg:w-[46%] relative"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(20px)",
              transition: "all 1s cubic-bezier(0.16, 1, 0.3, 1) 0.25s",
            }}
          >
            {/* Soft ambient glow */}
            <div
              className="absolute -inset-8 rounded-full pointer-events-none"
              style={{
                background: "radial-gradient(circle, rgba(255,112,67,0.05) 0%, transparent 65%)",
                filter: "blur(40px)",
              }}
            />

            <div className="relative grid grid-cols-12 grid-rows-[auto] gap-3 max-w-[520px] mx-auto">

              {/* ── Card 1: Main hero image (spans 7 cols, tall) ── */}
              <div
                className="col-span-7 row-span-2 rounded-[24px] overflow-hidden relative shadow-[0_16px_48px_rgba(0,0,0,0.08)] group"
                style={{
                  border: "1px solid rgba(255,255,255,0.6)",
                  aspectRatio: "3/4",
                  animation: "float-gentle 6s ease-in-out infinite",
                }}
              >
                <Image
                  src="/images/hero-community.png"
                  alt="Live community yoga session"
                  fill
                  priority
                  className="object-cover group-hover:scale-[1.03] transition-transform duration-700"
                  sizes="(max-width: 1024px) 60vw, 30vw"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />

                {/* Label inside main image */}
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="bg-white/90 backdrop-blur-md rounded-xl px-3 py-2 flex items-center gap-2.5">
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: "linear-gradient(135deg, #FF7043, #e8683a)" }}
                    >
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold text-[#2a2a2a] leading-tight">Live</p>
                      <p className="text-[9px] text-[#999]">Join expert sessions</p>
                    </div>
                    <span className="relative flex h-2 w-2 ml-auto">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                    </span>
                  </div>
                </div>
              </div>

              {/* ── Card 2: Meditation image (top-right, square) ── */}
              <div
                className="col-span-5 rounded-[20px] overflow-hidden relative shadow-[0_12px_36px_rgba(0,0,0,0.06)] group"
                style={{
                  border: "1px solid rgba(255,255,255,0.6)",
                  aspectRatio: "1/1",
                  animation: "float-gentle 7s ease-in-out infinite 1s",
                }}
              >
                <Image
                  src="/images/hero-meditation.png"
                  alt="Peaceful meditation session"
                  fill
                  className="object-cover group-hover:scale-[1.03] transition-transform duration-700"
                  sizes="(max-width: 1024px) 40vw, 20vw"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent" />
                <div className="absolute bottom-3 left-3">
                  <span className="text-[9px] font-semibold text-white/90 uppercase tracking-wider bg-black/20 backdrop-blur-sm px-2 py-1 rounded-md">
                    Meditation
                  </span>
                </div>
              </div>

              {/* ── Card 3: Bottom-right — AI assistant card ── */}
              <div
                className="col-span-5 rounded-[20px] overflow-hidden relative shadow-[0_12px_36px_rgba(0,0,0,0.06)]"
                style={{
                  border: "1px solid rgba(230,226,220,0.8)",
                  animation: "float-gentle 5s ease-in-out infinite 2s",
                }}
              >
                <div className="bg-[#2a2a2a] h-full flex flex-col justify-between p-4 relative overflow-hidden">
                  {/* Decorative glow inside */}
                  <div className="absolute -right-6 -bottom-6 w-24 h-24 rounded-full bg-[#FF7043]/10 blur-xl pointer-events-none" />

                  <div className="relative z-10">
                    <div className="flex items-center gap-1.5 mb-3">
                      <SparklesIcon className="w-3.5 h-3.5 text-[#FF7043]" />
                      <span className="text-[9px] text-[#999] font-semibold uppercase tracking-wider">Prana AI</span>
                    </div>

                    {/* Chat bubbles */}
                    <div className="space-y-2">
                      <div className="bg-white/[0.07] rounded-xl rounded-bl-sm px-2.5 py-1.5 w-[90%]">
                        <p className="text-[9px] text-[#bbb] leading-snug">Quick stretch for back pain?</p>
                      </div>
                      <div className="bg-[#FF7043]/15 rounded-xl rounded-br-sm px-2.5 py-1.5 w-[90%] ml-auto border border-[#FF7043]/10">
                        <p className="text-[9px] text-orange-200/90 leading-snug">Try Cat-Cow for 2 min — I can guide you!</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>

      {/* ═══ Animations ═══ */}
      <style jsx>{`
        @keyframes breathe {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.08); opacity: 0.7; }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes float-gentle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
      `}</style>
    </section>
  );
};

export default HomePageBanner;
